
import type { FacetsDTO, FacetsService } from '@empathyco/playboard-core-domain'
import type { Route } from 'vue-router'

import type { DataTableColumn, DataTableOption, GuardResolveFunction } from '../../models'

import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import CrudList from '../../components/crud/crud-list.component.vue'
import DataTable from '../../components/crud/data-table.component.vue'
import { EpbPill } from '../../components/shared/epb-pill'
import { useBrowseListFilter, useEndpoints } from '../../composables'
import { useToolingList } from '../../composables/use-tooling-list'
import { CONTAINER } from '../../config'
import { TYPES } from '../../config/inversify.types'
import { AlignEnum, EndpointContextEnum, FeatureIdEnum } from '../../enums'
import { ROUTER_CONSTS } from '../../router/router.consts'
import {
  getFacetsBrowseContextLabel,
  isBrowseLikeFacetsConfig,
} from '../../utils/facets-browse-context.utils'

export default defineComponent({
  name: 'FacetsList',
  components: { EpbPill, DataTable, CrudList },
  async beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction) {
    this.contextWatchDisabled = true

    next()
  },
  setup(props, ctx) {
    const baseClass = 'facets-list'

    const service = CONTAINER.get<FacetsService>(TYPES.FacetsService)

    const routes = ROUTER_CONSTS.routes[FeatureIdEnum.FACETS]

    const currentInstance = getCurrentInstance()

    const { emit } = ctx

    const messages = currentInstance.proxy.$epb.messages.tools.facets

    const { endpoints } = useEndpoints()

    const { browseExtraParams } = useBrowseListFilter()

    const loadDataExtraParams = computed(() => ({
      feature: 'faceting',
      endpointId: endpoints.value.map(endpoint => endpoint.value).join(','),
      ...browseExtraParams.value,
    }))

    const toolingList = useToolingList(props, ctx, {
      routes,
      messages,
      loadDataExtraParams,
    })

    watch(
      [endpoints, browseExtraParams],
      ([loadedEndpoints]) => {
        if (loadedEndpoints.length > 0) {
          toolingList.loadData()
        }
      },
      { immediate: true },
    )

    const crudListRef = ref()

    const columns = computed<Array<DataTableColumn>>(() => {
      return [
        {
          id: 'title',
          sortable: true,
          sortableId: 'title',
          text: messages.fields.name.label,
        },
        {
          id: 'endpointId',
          text: messages.fields.endpointId.label,
        },
        {
          id: 'context',
          text: endpoints.value.find(endpoint => endpoint.context === EndpointContextEnum.FILTER)
            ? messages.fields.context.label
            : messages.fields.queries.label,
          width: '352px',
        },
        ...toolingList.visiblePivotColumns.value,
        {
          id: 'metadata.author',
          text: messages.fields.user.label,
        },
        {
          id: 'metadata.updated',
          sortable: true,
          sortableId: 'updated',
          text: messages.fields.updateDate.label,
        },
        {
          id: 'criteria.enabled',
          text: messages.fields.enabled.label,
        },
        {
          id: 'options',
          width: '44px',
          align: AlignEnum.CENTER,
        },
      ]
    })

    function onOptionsClick(option: DataTableOption, dataItem): void {
      toolingList.onOptionClick(option, dataItem, crudListRef)
    }

    function getEndpointContext(endpointId: string): string | undefined {
      return endpoints.value.find(endpoint => endpoint.value === endpointId)?.context
    }

    function isSearchLikeItem(item: FacetsDTO): boolean {
      const endpointContext = getEndpointContext(item.endpointId)
      return (
        !isBrowseLikeFacetsConfig(item, endpointContext) &&
        (item.endpointId === DomainEnum.SEARCH || endpointContext === EndpointContextEnum.QUERY)
      )
    }

    function getBrowseContextLabel(item: FacetsDTO): string | undefined {
      if (!isBrowseLikeFacetsConfig(item, getEndpointContext(item.endpointId))) {
        return undefined
      }
      return getFacetsBrowseContextLabel(item.criteria.context)
    }

    async function updateItem(item: any): Promise<void> {
      try {
        await service.update(item)
      } catch (error) {
        emit('showError', {
          title: messages.errorEnabled.title,
          messages: [messages.errorEnabled.description],
        })
      }
    }

    async function setEnabledAndDisabledPreviousOneFacet(
      selectedItem,
      enabled: boolean,
    ): Promise<void> {
      const findItemSelected = findItemById(selectedItem.id)

      if (enabled) {
        const allData = await service.list({ strictMatching: true, feature: 'faceting' })
        await Promise.all(
          getEnabledConflicts(findItemSelected, allData.results).map(async item => {
            item.criteria.enabled = false
            await updateItem(item)
          }),
        )
      }

      findItemSelected.criteria.enabled = enabled
      await updateItem(findItemSelected)
    }

    function getSelectedItems() {
      if (toolingList.optionSelected.value) {
        return [toolingList.optionSelected.value]
      }
      return [...toolingList.selection.value]
    }

    function findItemById(id): FacetsDTO | undefined {
      return toolingList.data.value.results.find(item => item.id === id) as FacetsDTO | undefined
    }

    function isSameFacetConfiguration(item: FacetsDTO, other: FacetsDTO): boolean {
      return (
        JSON.stringify(item.criteria.pivots) === JSON.stringify(other.criteria.pivots) &&
        JSON.stringify(item.criteria.context) === JSON.stringify(other.criteria.context) &&
        item.endpointId === other.endpointId
      )
    }

    function getEnabledConflicts(facet: FacetsDTO, allDataResults: FacetsDTO[]): FacetsDTO[] {
      return allDataResults.filter(
        item =>
          item.id !== facet.id && item.criteria.enabled && isSameFacetConfiguration(facet, item),
      )
    }
    function syncSelectionEnabledState(enabled: boolean): void {
      const withEnabledState = item => ({ ...item, criteria: { ...item.criteria, enabled } })

      toolingList.selection.value = toolingList.selection.value.map(withEnabledState)
      if (toolingList.optionSelected.value) {
        toolingList.optionSelected.value = withEnabledState(toolingList.optionSelected.value)
      }
    }

    async function setEnabledAndDisabledPreviousFacets({ enabled, callFromModal }): Promise<void> {
      toolingList.loadingData.value = true

      try {
        const selectedItems = getSelectedItems()
        const itemsToUpdate = selectedItems.filter(item => {
          const current = findItemById(item.id)
          return current?.criteria?.enabled !== enabled
        })

        if (!itemsToUpdate.length) {
          return
        }

        if (enabled && crudListRef.value.$refs.enableDialog && !callFromModal) {
          const allData = await service.list({ strictMatching: true, feature: 'faceting' })
          const hasConflicts = itemsToUpdate.some(selectedItem => {
            const current = findItemById(selectedItem.id)
            return !!current && getEnabledConflicts(current, allData.results).length > 0
          })

          if (hasConflicts) {
            crudListRef.value.$refs.enableDialog.open()
            return
          }
        }

        await Promise.all(
          itemsToUpdate.map(selectedItem =>
            setEnabledAndDisabledPreviousOneFacet(selectedItem, enabled),
          ),
        )

        syncSelectionEnabledState(enabled)
        toolingList.resetPage()
        await toolingList.loadData()
      } catch (error) {
        emit('showError', currentInstance.proxy.$epb.utils.decodeErrorMessage(error))
      } finally {
        toolingList.loadingData.value = false
      }
    }

    return {
      ...toolingList,
      DomainEnum,
      baseClass,
      routes,
      messages,
      crudListRef,
      columns,
      onOptionsClick,
      isSearchLikeItem,
      getBrowseContextLabel,
      setEnabledAndDisabledPreviousFacets,
    }
  },
})
